import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Chat, Check, PaperPlaneTilt, PencilSimple } from '@phosphor-icons/react';
import { can } from '@mybusinessmate/schema';
import { Icon } from '../../../../../../components/Icon';
import { IdentityHeader, IdentityTile } from '../../../../../../components/shell/identity-header';
import { EditMilestonesDialog } from '../../../../../../components/tasks/milestone-editor';
import { ProgressTrack } from '../../../../../../components/tasks/progress-track';
import { TaskDetailsCard } from '../../../../../../components/tasks/task-details-card';
import { TeamPanel } from '../../../../../../components/tasks/team-panel';
import { ClientFeedbackBanner } from '../../../../../../components/tasks/client-feedback-banner';
import { VersionsPanel } from '../../../../../../components/tasks/versions-panel';
import { Banner } from '../../../../../../components/ui/banner';
import { Card } from '../../../../../../components/ui/card';
import { Chip } from '../../../../../../components/ui/chip';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../../components/ui/state-views';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { textLinkClass } from '../../../../../../components/ui/text-link';
import { useToast } from '../../../../../../components/ui/toast';
import { MoreButton, pillClass } from '../../../../../../components/ui/toolbar-controls';
import { formatDay } from '../../../../../../lib/project-display';
import { useSession } from '../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../lib/staff-nav';
import {
  activityIcon,
  clientStatus,
  dueLabel,
  relativeTime,
  staffStatus,
  TASK_TYPE_META,
} from '../../../../../../lib/task-display';
import { toMilestoneInputs } from '../../../../../../lib/task-milestones';
import {
  taskQuery,
  usePublishVersion,
  useSetMilestones,
  useSetProgress,
  versionsQuery,
} from '../../../../../../lib/tasks-api';
import { toasts } from '../../../../../../lib/toast-presets';

export const Route = createFileRoute('/subdomain/admin/_staff/tasks/$taskId/')({
  // Same 1200 canvas as the project page: the work area plus the right-hand column.
  staticData: { wide: true },
  component: TaskPage,
});

/** Where the progress card says the slider last moved, from the activity feed (newest first). */
function lastMove(activity: { message: string; authorName: string | null; createdAt: string }[]) {
  const entry = activity.find((a) => /^moved progress/i.test(a.message));
  const to = entry ? /to (\d+)%/i.exec(entry.message)?.[1] : undefined;
  return entry && to ? { to, by: entry.authorName, at: entry.createdAt } : null;
}

/**
 * Staff task page (Figma A05.4): the progress track with its milestones, the version-0 brief, the activity, the
 * team and the details. The slider and the milestone ticks are the people on the task's; the plan and the team are
 * the account manager's and admins'. The server works out which (`task.access`), the page only shows it.
 * Versions v1+ (drafts, lead QA, "Approve & send to client") are the review gate; the in-place hotfix and task chat come later.
 */
function TaskPage() {
  const { taskId } = Route.useParams();
  const { data: session } = useSession();
  const role = session?.user.role;
  const toast = useToast();
  const { data: task, isLoading, error, refetch } = useQuery(taskQuery(taskId));
  const { data: versions = [] } = useQuery({ ...versionsQuery(taskId), enabled: !!task });
  const publish = usePublishVersion(taskId);
  const setProgress = useSetProgress(taskId);
  const setMilestones = useSetMilestones(taskId);
  const [planning, setPlanning] = useState(false);
  const soon = (description: string) => () => toast(toasts.comingSoon(description));
  const failed = () => toast({ title: 'Couldn’t save that', description: 'Please try again.', tone: 'error' });

  if (isLoading) return <PageSkeleton />;

  if (!task) {
    return (
      <LoadFailure
        error={error}
        noun="task"
        forbiddenHint="Ask an admin to assign you to it."
        onRetry={() => void refetch()}
      />
    );
  }

  const open = versions.find((v) => v.internalStatus === 'draft' || v.internalStatus === 'pending_lead_review');
  const pending = open?.internalStatus === 'pending_lead_review' ? open : undefined;
  // While a version waits on the lead the task is still "In progress" for the client, but the team calls it Lead QA.
  const status = pending ? { label: 'Lead QA', tone: 'blue' as const } : staffStatus(task.status);
  const type = TASK_TYPE_META[task.taskType];
  const due = dueLabel(task.deadline);
  const isDraft = task.status === 'User Draft';
  const isClosed = isDraft || task.status === 'Cancelled';
  const canEdit =
    isDraft && isStaffRoleName(role) && can({ role }, 'task.create', { managesBusiness: role === 'account_manager' });
  // The handle follows the response, but not before it: while a save is in flight it stays where it was dropped.
  const progress = setProgress.isPending ? (setProgress.variables?.progress ?? task.progress) : task.progress;
  const move = lastMove(task.activity);
  const hasWork = !isClosed && task.status !== 'Completed' && task.access.work;
  const businessId = task.businessId;

  const toggleMilestone = (index: number) => {
    const next = toMilestoneInputs(task.milestones).map((m, i) =>
      i === index ? { ...m, completed: !m.completed } : m,
    );
    setMilestones.mutate(next, { onError: failed });
  };

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-(--mbm-space-2) text-label-medium text-text-secondary"
      >
        <Link
          to="/subdomain/admin/businesses"
          search={{ tab: undefined, q: undefined, am: undefined }}
          className="inline-flex items-center gap-(--mbm-space-2) hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={ArrowLeft} size="sm" />
          Businesses
        </Link>
        {businessId ? (
          <>
            <span aria-hidden>/</span>
            <Link
              to="/subdomain/admin/businesses/$businessId"
              params={{ businessId }}
              className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              {task.businessName ?? 'Business'}
            </Link>
          </>
        ) : null}
        <span aria-hidden>/</span>
        <Link
          to="/subdomain/admin/projects/$projectId"
          params={{ projectId: task.projectId }}
          search={{}}
          className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          {task.projectName}
        </Link>
        <span aria-hidden>/</span>
        <span className="text-text-primary">{task.title}</span>
      </nav>

      <IdentityHeader
        tile={
          <IdentityTile>
            <Icon icon={type.icon} size="xl" />
          </IdentityTile>
        }
        title={task.title}
        meta={
          <>
            <StatusTag
              size="md"
              label={status.label}
              tone={status.tone}
              variant={status.tone === 'grey' ? 'outline' : 'soft'}
            />
            <Chip size="md">{type.label}</Chip>
            <span>
              {task.brief?.authorName ? `Created by ${task.brief.authorName} on ` : 'Created '}
              {formatDay(task.createdAt)}
              {due ? ` · ${due.text}` : ''}
            </span>
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={soon('Task chat arrives with the messaging step.')}
              className={pillClass('light', 'lg')}
            >
              <Icon icon={Chat} size="sm" />
              Task chat
            </button>
            {canEdit ? (
              <Link to="/subdomain/admin/tasks/$taskId/edit" params={{ taskId }} className={pillClass('dark', 'lg')}>
                <Icon icon={PencilSimple} size="sm" />
                Continue editing
              </Link>
            ) : pending && task.access.publish ? (
              <button
                type="button"
                disabled={publish.isPending}
                onClick={() =>
                  publish.mutate(
                    { subId: pending.id },
                    {
                      onSuccess: () => toast({ title: 'Sent to the client', tone: 'success' }),
                      onError: failed,
                    },
                  )
                }
                className={pillClass('dark', 'lg')}
              >
                <Icon icon={Check} size="sm" />
                Approve & send to client
              </button>
            ) : hasWork && !pending && ['In Progress', 'Revision Requested'].includes(task.status) ? (
              <Link to="/subdomain/admin/tasks/$taskId/submit" params={{ taskId }} className={pillClass('dark', 'lg')}>
                <Icon icon={PaperPlaneTilt} size="sm" />
                {open ? `Continue v${open.versionNumber}` : 'Submit a version'}
              </Link>
            ) : null}
            <MoreButton onClick={soon('More actions arrive with the review step.')} />
          </>
        }
      />

      {isDraft ? (
        <Banner tone="info" title="This is a draft">
          The team and the client don’t see it yet. Submit it to send it to intake.
        </Banner>
      ) : null}

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex flex-col gap-(--mbm-space-5)">
          <ClientFeedbackBanner task={task} versions={versions} />
          <Card tone="panel" padding="lg" className="flex flex-col gap-(--mbm-space-5)">
            <div className="flex items-start justify-between gap-(--mbm-space-4)">
              <div className="flex min-w-0 flex-col gap-(--mbm-space-1)">
                <h2 className="text-heading-h3 text-text-primary">Progress</h2>
                <p className="text-body-small text-text-secondary">
                  {isDraft
                    ? 'Progress starts once the task is submitted.'
                    : move
                      ? `Moved to ${move.to}% by ${move.by ?? 'the team'} · ${relativeTime(move.at)}.`
                      : 'Nobody has moved it yet.'}{' '}
                  {task.clientVisible && !isDraft
                    ? `The client sees “${clientStatus(task.status).label}” in their app.`
                    : ''}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-(--mbm-space-1)">
                <span className="text-heading-h1 text-text-strong">{progress}%</span>
                {task.access.manage && !isClosed ? (
                  <button type="button" onClick={() => setPlanning(true)} className={textLinkClass}>
                    Edit milestones
                  </button>
                ) : null}
              </div>
            </div>
            <ProgressTrack
              value={progress}
              milestones={task.milestones}
              interactive={hasWork}
              disabled={setProgress.isPending || setMilestones.isPending}
              onCommit={(next) => setProgress.mutate({ progress: next }, { onError: failed })}
              onToggle={toggleMilestone}
            />
          </Card>

          <VersionsPanel task={task} versions={versions} canEditBrief={canEdit} />

          <Card tone="panel" padding="lg" className="flex flex-col gap-(--mbm-space-3)">
            <div className="flex items-center justify-between gap-(--mbm-space-3)">
              <h2 className="text-heading-h3 text-text-primary">Activity</h2>
              <button
                type="button"
                onClick={soon('The full audit log arrives with the audit step.')}
                className={textLinkClass}
              >
                Full audit log
              </button>
            </div>
            {task.activity.length === 0 ? (
              <p className="text-body-medium text-text-secondary">Nothing yet.</p>
            ) : (
              <ul className="flex flex-col">
                {task.activity.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center gap-(--mbm-space-3) border-b border-border-line py-(--mbm-space-3) last:border-b-0"
                  >
                    <Icon icon={activityIcon(entry.message)} size="md" />
                    <span className="min-w-0 flex-1 text-body-medium text-text-primary">
                      {/* A client's own action is logged under a staff member's id (activity belongs to staff), so it names nobody. */}
                      {entry.authorName && entry.designation !== 'Client' ? `${entry.authorName} ` : ''}
                      {entry.designation === 'Client'
                        ? entry.message
                        : entry.message.charAt(0).toLowerCase() + entry.message.slice(1)}
                    </span>
                    {entry.kind === 'hotfix' ? (
                      <span className="shrink-0 rounded-(--mbm-radius-xs) border border-border-default px-(--mbm-space-2) text-label-small text-text-secondary">
                        Hotfix · client not notified
                      </span>
                    ) : null}
                    <span className="shrink-0 text-body-small text-text-tertiary">{relativeTime(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-(--mbm-space-4)">
          <TeamPanel task={task} canSuggest={role === 'lead_specialist' && task.access.work} />
          <TaskDetailsCard task={task} />
          <Card tone="panel" className="flex flex-col gap-(--mbm-space-3)">
            <div className="flex items-center justify-between gap-(--mbm-space-3)">
              <h2 className="text-heading-h3 text-text-primary">Task chat</h2>
              <button
                type="button"
                onClick={soon('Task chat arrives with the messaging step.')}
                className={textLinkClass}
              >
                Open
              </button>
            </div>
            <p className="text-body-small text-text-tertiary">
              Internal notes are only visible to staff. Client messages show here in their app.
            </p>
          </Card>
        </div>
      </div>

      {task.access.manage && !isClosed ? (
        <EditMilestonesDialog
          key={String(planning)}
          open={planning}
          onClose={() => setPlanning(false)}
          taskId={task.id}
          type={task.taskType}
          initial={toMilestoneInputs(task.milestones)}
        />
      ) : null}
    </>
  );
}
